/* ============================================================================
 * sp-pieza-3d.js — Sportiva .925
 * ----------------------------------------------------------------------------
 * Visor 3D del configurador de grabado.
 *
 * QUÉ HACE
 *   Dibuja la pieza elegida (luneta, cuadrado, estrella, esfera, botón,
 *   corazón, cubo o dije) en plata o en oro, y le pone encima lo que la
 *   clienta escribe en cada cara, como si ya estuviera grabado. Se gira
 *   arrastrando, y al escribir en una cara la pieza voltea sola hacia esa cara.
 *
 * DE DÓNDE SACA LOS DATOS
 *   No lee el formulario: escucha el evento `sp-cfg:estado` que dispara
 *   sp-configurador.js cada vez que algo cambia, y `sp-cfg:cara` cuando se
 *   enfoca un campo. Si el navegador no tiene WebGL, el visor no aparece y el
 *   configurador sigue mostrando las placas de siempre.
 *
 * LOS MODELOS SON ILUSTRATIVOS
 *   Proporciones tomadas de las fotos de producto, no de los planos del
 *   taller. Sirven para ver dónde y cómo queda el grabado, no para medir.
 *
 * SIN DEPENDENCIAS EXTRA
 *   Solo el núcleo de three.js. El giro con el dedo, el estudio de luz y los
 *   bordes redondeados están hechos aquí para no cargar complementos.
 * ========================================================================== */

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

/* ------------------------------------------------------------------ metal */

const METAL = {
  plata: { color: 0xf0f2f4, rugosidad: 0.2, tinta: '#3d4044', luz: 'rgba(255,255,255,.55)' },
  oro:   { color: 0xf0c987, rugosidad: 0.24, tinta: '#5e4417', luz: 'rgba(255,240,205,.55)' }
};

/* Estudio de fotografía de bolsillo: una caja oscura con paneles de luz.
   Es lo que hace que el metal refleje algo y no se vea plano. */
function estudio(renderer) {
  const pm = new THREE.PMREMGenerator(renderer);
  const sc = new THREE.Scene();
  const caja = new THREE.BoxGeometry(1, 1, 1);
  sc.add(new THREE.Mesh(new THREE.BoxGeometry(12, 12, 12), new THREE.MeshBasicMaterial({ color: 0x4a4644, side: THREE.BackSide })));
  const panel = (k, x, y, z, sx, sy, sz) => {
    const m = new THREE.Mesh(caja, new THREE.MeshBasicMaterial({ color: new THREE.Color(k, k, k) }));
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); sc.add(m);
  };
  panel(9, 0, 5.5, 0, 8, 0.1, 5);      /* softbox arriba */
  panel(5, -5.5, 1, 1, 0.1, 5, 2.5);   /* tira izquierda */
  panel(4, 5.5, 0.5, -1, 0.1, 4, 2);   /* tira derecha */
  panel(2.5, 0, -1, 5.5, 6, 1.2, 0.1); /* rebote frontal */
  const tex = pm.fromScene(sc, 0.035).texture;
  pm.dispose();
  return tex;
}

/* ----------------------------------------------------------------- formas */

function circulo(r) {
  const s = new THREE.Shape(); s.absarc(0, 0, r, 0, Math.PI * 2, false); return s;
}

function rectRedondo(w, h, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

function estrella(R, r) {
  const s = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = Math.PI / 2 + i * Math.PI / 5, rr = i % 2 ? r : R;
    const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
    i ? s.lineTo(x, y) : s.moveTo(x, y);
  }
  s.closePath();
  return s;
}

/* Corazón clásico, centrado y con la punta hacia abajo. `w` es el ancho. */
function corazon(w) {
  const k = 110 / w, P = (x, y) => [(x - 25) / k, -(y - 47.5) / k];
  const s = new THREE.Shape();
  s.moveTo(...P(25, 25));
  s.bezierCurveTo(...P(25, 25), ...P(20, 0), ...P(0, 0));
  s.bezierCurveTo(...P(-30, 0), ...P(-30, 35), ...P(-30, 35));
  s.bezierCurveTo(...P(-30, 55), ...P(-10, 77), ...P(25, 95));
  s.bezierCurveTo(...P(60, 77), ...P(80, 55), ...P(80, 35));
  s.bezierCurveTo(...P(80, 35), ...P(80, 0), ...P(50, 0));
  s.bezierCurveTo(...P(35, 0), ...P(25, 25), ...P(25, 25));
  return s;
}

function flor(R) {
  const s = new THREE.Shape(), n = 240;
  for (let i = 0; i <= n; i++) {
    const a = i / n * Math.PI * 2 + Math.PI / 2;
    const rr = R * (0.66 + 0.34 * Math.pow(Math.abs(Math.cos(2.5 * (a - Math.PI / 2))), 0.7));
    const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
    i ? s.lineTo(x, y) : s.moveTo(x, y);
  }
  return s;
}

/* Extruye una silueta, la centra en z y devuelve dónde queda la cara. */
function placa(shape, grosor, bisel, segBisel) {
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: grosor, bevelEnabled: bisel > 0, bevelThickness: bisel, bevelSize: bisel * 0.9,
    bevelSegments: segBisel || 6, curveSegments: 64
  });
  g.translate(0, 0, -grosor / 2);
  g.computeVertexNormals();
  return { geo: g, cara: grosor / 2 + bisel };
}

/* ------------------------------------------------------------------ piezas */
/* Cada pieza devuelve su malla, las caras grabables (dónde y de qué tamaño
   va el grabado) y hasta dónde llega por arriba para colgarle la argolla. */

function pieza(forma, formaDije) {
  const P = { cuerpos: [], caras: [], tope: 0, argolla: 'grande' };
  const frente = (z, tam, extra) => Object.assign({ pos: new THREE.Vector3(0, 0, z), giro: 0, tam }, extra || {});
  const reverso = (z, tam, extra) => Object.assign({ pos: new THREE.Vector3(0, 0, -z), giro: Math.PI, tam }, extra || {});
  let p;

  switch (forma) {
    case 'luneta_grande':
    case 'luneta_chica': {
      const r = forma === 'luneta_grande' ? 0.82 : 0.66;
      p = placa(circulo(r), 0.16, 0.06);
      P.cuerpos.push(p.geo); P.tope = r;
      P.caras.push(frente(p.cara, r * 1.55), reverso(p.cara, r * 1.55));
      break;
    }
    case 'cuadrado_chico': {
      p = placa(rectRedondo(1.12, 1.12, 0.2), 0.42, 0.08);
      P.cuerpos.push(p.geo); P.tope = 0.64;
      P.caras.push(frente(p.cara, 1.0), reverso(p.cara, 1.0));
      break;
    }
    case 'estrella': {
      p = placa(estrella(0.92, 0.42), 0.09, 0.035, 4);
      P.cuerpos.push(p.geo); P.tope = 0.92;
      P.caras.push(frente(p.cara, 0.78, { bajar: 0.06 }), reverso(p.cara, 0.78, { bajar: 0.06 }));
      break;
    }
    case 'esfera': {
      P.cuerpos.push(new THREE.SphereGeometry(0.66, 96, 64)); P.tope = 0.66;
      P.caras.push({ esfera: 0.66, giro: 0 }, { esfera: 0.66, giro: Math.PI });
      break;
    }
    case 'circulo_boton': {
      p = placa(circulo(0.7), 0.36, 0.12, 8);
      const g = p.geo; P.cuerpos.push(g); P.tope = 0.7; P.argolla = null; P.boton = 0.7;
      P.caras.push(frente(p.cara, 1.08), reverso(p.cara, 1.08));
      break;
    }
    case 'corazon_colgante':
    case 'corazon_boton': {
      p = placa(corazon(1.5), 0.18, 0.22, 12);
      P.cuerpos.push(p.geo); P.tope = 0.2045 * 1.5 + 0.2;
      if (forma === 'corazon_boton') { P.argolla = null; P.boton = 0.62; }
      P.caras.push(frente(p.cara, 0.86, { bajar: -0.06 }), reverso(p.cara, 0.86, { bajar: -0.06 }));
      break;
    }
    case 'cubo': {
      const lado = 0.84;
      p = placa(rectRedondo(lado, lado, 0.08), lado, 0.08, 5);
      P.cuerpos.push(p.geo); P.tope = lado / 2 + 0.08;
      const z = p.cara, t = 0.82;
      P.caras.push(
        { pos: new THREE.Vector3(0, 0, z), giro: 0, tam: t },
        { pos: new THREE.Vector3(z, 0, 0), giro: Math.PI / 2, tam: t },
        { pos: new THREE.Vector3(0, 0, -z), giro: Math.PI, tam: t },
        { pos: new THREE.Vector3(-z, 0, 0), giro: -Math.PI / 2, tam: t }
      );
      break;
    }
    case 'dije': {
      const f = (formaDije || '').toLowerCase();
      let s, tope, tam;
      if (f.indexOf('coraz') === 0) { s = corazon(1.7); tope = 0.2045 * 1.7 + 0.05; tam = 1.0; }
      else if (f.indexOf('flor') === 0) { s = flor(0.9); tope = 0.9; tam = 0.95; }
      else if (f.indexOf('estrella') === 0) { s = estrella(0.95, 0.45); tope = 0.95; tam = 0.8; }
      else if (f.indexOf('vertical') > -1) { s = rectRedondo(0.86, 1.4, 0.12); tope = 0.7; tam = 0.8; }
      else if (f.indexOf('horizontal') > -1) { s = rectRedondo(1.5, 0.86, 0.12); tope = 0.43; tam = 1.35; }
      else if (f.indexOf('texto') === 0) return null;
      else { s = circulo(0.85); tope = 0.85; tam = 1.3; }
      p = placa(s, 0.05, 0.02, 3);
      P.cuerpos.push(p.geo); P.tope = tope; P.argolla = 'chica';
      P.caras.push(frente(p.cara, tam), reverso(p.cara, tam));
      break;
    }
    default:
      return null;
  }
  return P;
}

/* ---------------------------------------------------------------- grabado */

const figuras = {};
function cargarFigura(src) {
  if (figuras[src]) return figuras[src];
  figuras[src] = new Promise((ok) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => ok(im);
    im.onerror = () => ok(null);
    im.src = src.indexOf('//') === 0 ? 'https:' + src : src;
  });
  return figuras[src];
}

/* Pinta en un lienzo transparente lo que va en una cara: texto en versales
   espaciadas (como la placa de siempre) o la silueta de la figura. Un filo
   claro debajo del trazo oscuro es lo que hace que se lea como incisión. */
function pintarGrabado(cv, cara, metal, fuente, ancho) {
  const S = cv.width, cx = cv.getContext('2d');
  cx.clearRect(0, 0, S, S);
  const M = METAL[metal];
  if (cara.figura) {
    const im = cara.figura, lim = S * 0.62 * ancho;
    const k = Math.min(lim / im.naturalWidth, lim / im.naturalHeight);
    const w = im.naturalWidth * k, h = im.naturalHeight * k;
    const tmp = document.createElement('canvas'); tmp.width = S; tmp.height = S;
    const t = tmp.getContext('2d');
    t.drawImage(im, (S - w) / 2, (S - h) / 2, w, h);
    t.globalCompositeOperation = 'source-in';
    t.fillStyle = M.luz; t.fillRect(0, 0, S, S);
    cx.drawImage(tmp, 0, S * 0.006);
    t.globalCompositeOperation = 'source-over';
    t.clearRect(0, 0, S, S);
    t.drawImage(im, (S - w) / 2, (S - h) / 2, w, h);
    t.globalCompositeOperation = 'source-in';
    t.fillStyle = M.tinta; t.fillRect(0, 0, S, S);
    cx.drawImage(tmp, 0, 0);
    return;
  }
  const txt = (cara.texto || '').toUpperCase().trim();
  if (!txt) return;
  const sep = 0.1;
  cx.textAlign = 'center'; cx.textBaseline = 'middle';
  let fs = 100;
  cx.font = '600 ' + fs + 'px ' + fuente;
  const medir = () => cx.measureText(txt).width + sep * fs * (txt.length - 1);
  const lim = S * 0.8 * ancho;
  fs = Math.min(S * 0.3, fs * lim / medir());
  cx.font = '600 ' + fs + 'px ' + fuente;
  if ('letterSpacing' in cx) cx.letterSpacing = (sep * fs) + 'px';
  const y = S / 2 + fs * 0.04;
  cx.fillStyle = M.luz; cx.fillText(txt, S / 2, y + Math.max(2, fs * 0.05));
  cx.fillStyle = M.tinta; cx.fillText(txt, S / 2, y);
}

/* ------------------------------------------------------------------ visor */

function Visor(raiz, nodo) {
  this.raiz = raiz;
  this.nodo = nodo;
  this.lienzoHost = nodo.querySelector('[data-sp-cfg-3d-lienzo]');
  this.botones = nodo.querySelector('[data-sp-cfg-3d-caras]');
  this.columnas = raiz.querySelector('[data-sp-cfg-dos]');
  this.fuente = (getComputedStyle(raiz).getPropertyValue('--sp-fuente-titulo') || '').trim() || 'sans-serif';

  const r = this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  r.outputColorSpace = THREE.SRGBColorSpace;
  r.toneMapping = THREE.ACESFilmicToneMapping;
  r.toneMappingExposure = 1.18;
  this.lienzoHost.appendChild(r.domElement);

  this.escena = new THREE.Scene();
  this.escena.environment = estudio(r);
  this.camara = new THREE.PerspectiveCamera(28, 1, 0.1, 50);
  this.camara.position.set(0, 0.35, 6.2);
  this.camara.lookAt(0, 0.25, 0);
  const sol = new THREE.DirectionalLight(0xffffff, 0.6); sol.position.set(2, 4, 5); this.escena.add(sol);

  /* Sombra suave bajo la pieza: un degradado, no un cálculo de sombras. */
  const cs = document.createElement('canvas'); cs.width = cs.height = 128;
  const g = cs.getContext('2d'), rg = g.createRadialGradient(64, 64, 4, 64, 64, 62);
  rg.addColorStop(0, 'rgba(46,2,2,.22)'); rg.addColorStop(1, 'rgba(46,2,2,0)');
  g.fillStyle = rg; g.fillRect(0, 0, 128, 128);
  this.sombra = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 0.9), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(cs), transparent: true, depthWrite: false }));
  this.sombra.rotation.x = -Math.PI / 2;
  this.escena.add(this.sombra);

  this.grupo = new THREE.Group();
  this.escena.add(this.grupo);

  this.giroY = 0; this.giroX = 0.06;   /* hacia donde quiere ir */
  this.objY = 0; this.objX = 0.06;     /* hacia donde se le pidió */
  this.arrastrando = false; this.ultimoToque = 0;
  this.clave = ''; this.caras = []; this.metal = 'plata';

  this.enlazar();
  this.medir();
  this.animar = this.animar.bind(this);
  requestAnimationFrame(this.animar);
}

Visor.prototype.medir = function () {
  const w = this.lienzoHost.clientWidth || 300, h = this.lienzoHost.clientHeight || 300;
  this.renderer.setSize(w, h, false);
  this.camara.aspect = w / h;
  this.camara.updateProjectionMatrix();
};

Visor.prototype.enlazar = function () {
  const el = this.renderer.domElement;
  let x0 = 0, y0 = 0;
  el.style.touchAction = 'pan-y';
  el.addEventListener('pointerdown', (e) => {
    this.arrastrando = true; x0 = e.clientX; y0 = e.clientY;
    el.setPointerCapture(e.pointerId);
  });
  el.addEventListener('pointermove', (e) => {
    if (!this.arrastrando) return;
    this.objY += (e.clientX - x0) * 0.012;
    this.objX = Math.max(-0.7, Math.min(0.7, this.objX + (e.clientY - y0) * 0.008));
    x0 = e.clientX; y0 = e.clientY;
    this.ultimoToque = performance.now();
  });
  const soltar = () => { this.arrastrando = false; this.ultimoToque = performance.now(); };
  el.addEventListener('pointerup', soltar);
  el.addEventListener('pointercancel', soltar);
  if ('ResizeObserver' in window) new ResizeObserver(() => this.medir()).observe(this.lienzoHost);
  else window.addEventListener('resize', () => this.medir());
};

/* Voltea la pieza hacia una cara por el camino más corto. */
Visor.prototype.mirar = function (i) {
  const c = this.caras[i];
  if (!c) return;
  const meta = -c.giro;
  let d = (meta - this.objY) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  this.objY += d; this.objX = 0.06;
  this.ultimoToque = performance.now();
  this.activa = i;
  [].forEach.call(this.botones.children, (b, k) => b.setAttribute('aria-pressed', k === i ? 'true' : 'false'));
};

Visor.prototype.limpiar = function () {
  this.grupo.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) {
      if (o.material.map && o.material.map !== this.texSombra) o.material.map.dispose();
      o.material.dispose();
    }
  });
  this.grupo.clear();
};

Visor.prototype.construir = function (forma, formaDije, metal, nCaras) {
  this.limpiar();
  const P = pieza(forma, formaDije);
  if (!P) return false;
  const M = METAL[metal];
  const mat = new THREE.MeshStandardMaterial({ color: M.color, metalness: 1, roughness: M.rugosidad, envMapIntensity: 1.15 });
  this.matMetal = mat;

  P.cuerpos.forEach((g) => this.grupo.add(new THREE.Mesh(g, mat)));

  /* Argolla: una chica que abraza la pieza y la grande de donde se cuelga. */
  if (P.argolla) {
    const chica = P.argolla === 'chica';
    const a1 = new THREE.Mesh(new THREE.TorusGeometry(chica ? 0.1 : 0.13, chica ? 0.025 : 0.038, 20, 48), mat);
    a1.rotation.y = Math.PI / 2;
    a1.position.y = P.tope + (chica ? 0.06 : 0.08);
    this.grupo.add(a1);
    if (!chica) {
      const a2 = new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.075, 24, 64), mat);
      a2.position.y = P.tope + 0.08 + 0.13 + 0.25 - 0.02;
      this.grupo.add(a2);
    }
  }
  /* Un botón se ensarta: se dibuja el tramo de cadena que lo atraviesa. */
  if (P.boton) {
    const cad = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 3.2, 16), new THREE.MeshStandardMaterial({ color: M.color, metalness: 1, roughness: 0.35 }));
    cad.rotation.z = Math.PI / 2;
    this.grupo.add(cad);
  }

  /* Capas de grabado: un lienzo por cara, encima de la superficie. */
  this.caras = P.caras.slice(0, nCaras).map((c) => {
    const cv = document.createElement('canvas'); cv.width = cv.height = 1024;
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    const m = new THREE.MeshStandardMaterial({
      map: tex, transparent: true, metalness: 0.6, roughness: 0.55,
      depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4
    });
    let malla;
    if (c.esfera) {
      const L = 1.5, A = 1.0, rr = c.esfera * 1.002;
      malla = new THREE.Mesh(new THREE.SphereGeometry(rr, 48, 32, Math.PI / 2 - L / 2 + c.giro, L, Math.PI / 2 - A / 2, A), m);
      c.ancho = 1;
    } else {
      malla = new THREE.Mesh(new THREE.PlaneGeometry(c.tam, c.tam), m);
      malla.position.copy(c.pos);
      if (c.bajar) malla.position.y -= c.bajar;
      malla.position.addScaledVector(c.pos.clone().normalize(), 0.003);
      malla.rotation.y = c.giro;
      c.ancho = 1;
    }
    this.grupo.add(malla);
    return Object.assign({}, c, { cv, tex });
  });

  /* Centra la pieza en la vista: la argolla también cuenta. */
  const caja = new THREE.Box3().setFromObject(this.grupo);
  const alto = caja.max.y - caja.min.y;
  this.grupo.position.y = -(caja.min.y + alto / 2) + 0.25;
  const esc = Math.min(1.25, 2.2 / Math.max(alto, caja.max.x - caja.min.x));
  this.grupo.scale.setScalar(esc);
  this.sombra.position.y = this.grupo.position.y + caja.min.y * esc - 0.18;
  return true;
};

Visor.prototype.pintarBotones = function (n) {
  const nombres = n <= 1 ? ['Grabado'] : n === 2 ? ['Frente', 'Reverso'] : Array.from({ length: n }, (_, i) => 'Cara ' + (i + 1));
  this.botones.innerHTML = '';
  nombres.forEach((t, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'sp-cfg__visor-cara'; b.textContent = t;
    b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
    b.addEventListener('click', () => this.mirar(i));
    this.botones.appendChild(b);
  });
};

Visor.prototype.actualizar = function (e) {
  if (!e || !e.forma) return;
  const metal = e.metal === 'oro' ? 'oro' : 'plata';
  const clave = e.forma + '|' + (e.formaDije || '') + '|' + e.caras.length;
  const sinVisor = e.texto || !pieza(e.forma, e.formaDije);
  this.nodo.hidden = sinVisor;
  if (this.columnas) this.columnas.classList.toggle('sp-cfg__dos--3d', !sinVisor);
  this.raiz.classList.toggle('sp-cfg--3d', !sinVisor);
  if (sinVisor) { this.clave = ''; return; }

  if (clave !== this.clave) {
    this.clave = clave;
    this.metal = metal;
    this.construir(e.forma, e.formaDije, metal, e.caras.length);
    this.pintarBotones(this.caras.length);
    this.objY = this.giroY = 0.5; this.objX = 0.06;
    this.mirar(0);
    this.medir();
  } else if (metal !== this.metal) {
    this.metal = metal;
    this.matMetal.color.setHex(METAL[metal].color);
    this.matMetal.roughness = METAL[metal].rugosidad;
    this.grupo.traverse((o) => { if (o.material && o.material !== this.matMetal && !o.material.map) o.material.color.setHex(METAL[metal].color); });
  }

  e.caras.forEach((dato, i) => {
    const c = this.caras[i];
    if (!c) return;
    const firma = metal + '|' + (dato.texto || '') + '|' + (dato.imagen || '');
    if (firma === c.firma) return;
    const cambioTexto = c.firma && (c.textoPrevio !== dato.texto || c.imgPrevia !== dato.imagen);
    c.firma = firma; c.textoPrevio = dato.texto; c.imgPrevia = dato.imagen;
    const pintar = (fig) => {
      pintarGrabado(c.cv, { texto: dato.texto, figura: fig }, metal, this.fuente, c.ancho);
      c.tex.needsUpdate = true;
    };
    if (dato.imagen) cargarFigura(dato.imagen).then((im) => { if (c.firma === firma) pintar(im); });
    else pintar(null);
    if (cambioTexto && this.activa !== i) this.mirar(i);
  });
};

Visor.prototype.animar = function (t) {
  requestAnimationFrame(this.animar);
  if (this.nodo.hidden || !this.nodo.offsetParent) return;
  /* Quieta no se ve que es 3D: si nadie la toca, se mece un poco. */
  const quieto = !this.arrastrando && t - this.ultimoToque > 2500;
  const vaiven = quieto ? Math.sin((t - this.ultimoToque) / 1400) * 0.32 : 0;
  this.giroY += (this.objY + vaiven - this.giroY) * 0.09;
  this.giroX += (this.objX - this.giroX) * 0.09;
  this.grupo.rotation.set(this.giroX, this.giroY, 0);
  this.renderer.render(this.escena, this.camara);
};

/* -------------------------------------------------------------- arranque */

function soportaWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) { return false; }
}

/* Lee el estado directamente del configurador (sp-configurador.js guarda su
   instancia en raiz.__spCfg). Así este archivo no obliga a tocar aquel. */
function leerEstado(C) {
  if (!C || !C.pieza) return null;
  return {
    forma: C.pieza.forma,
    formaDije: C.opciones && (C.opciones.Forma || C.opciones.forma) || '',
    metal: C.raiz.getAttribute('data-metal') || 'plata',
    texto: typeof C.esTexto === 'function' ? C.esTexto() : false,
    caras: (C.caras || []).map((c) => ({ texto: c.texto || '', imagen: c.imagen && c.imagen.src || '' }))
  };
}

/* Arma la columna del visor dentro del paso 2: lo que había del formulario
   (opciones, caras, desglose, botón) pasa a la columna derecha. */
const NOTA = 'Modelo de referencia: la pieza real puede variar un poco en proporción y acabado.';
function montar(raiz) {
  let nodo = raiz.querySelector('[data-sp-cfg-3d]');
  if (nodo) return nodo;
  const paso = raiz.querySelector('[data-sp-cfg-paso="2"]');
  const inicio = paso && paso.querySelector('[data-sp-cfg-opciones]');
  if (!inicio) return null;
  const dos = document.createElement('div');
  dos.className = 'sp-cfg__dos'; dos.setAttribute('data-sp-cfg-dos', '');
  const col = document.createElement('div');
  col.className = 'sp-cfg__visor-col';
  const nota = (document.querySelector('[data-sp-visor-nota]') || {}).textContent || NOTA;
  col.innerHTML =
    '<div class="sp-cfg__visor" data-sp-cfg-3d hidden>' +
      '<div class="sp-cfg__lienzo3d" data-sp-cfg-3d-lienzo></div>' +
      '<p class="sp-cfg__visor-tip"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4"/></svg>Arrastra para girarla</p>' +
      '<div class="sp-cfg__visor-caras" data-sp-cfg-3d-caras></div>' +
    '</div>' +
    '<p class="sp-cfg__visor-nota"></p>';
  col.querySelector('.sp-cfg__visor-nota').textContent = nota.trim();
  const form = document.createElement('div');
  form.className = 'sp-cfg__form';
  paso.insertBefore(dos, inicio);
  let n = inicio;
  while (n) { const sig = n.nextSibling; form.appendChild(n); n = sig; }
  dos.appendChild(col); dos.appendChild(form);
  return col.querySelector('[data-sp-cfg-3d]');
}

function conectar(raiz, intentos) {
  const C = raiz.__spCfg;
  if (!C) {
    if (intentos < 60) setTimeout(() => conectar(raiz, intentos + 1), 100);
    return;
  }
  if (raiz.__spVisor) return;
  const nodo = montar(raiz);
  if (!nodo) return;
  let v;
  try { v = raiz.__spVisor = new Visor(raiz, nodo); } catch (e) { return; }

  /* Cada cambio del formulario pasa por revisar(): ahí se engancha. */
  const original = C.revisar;
  C.revisar = function () {
    const r = original.apply(this, arguments);
    try { v.actualizar(leerEstado(C)); } catch (e) {}
    return r;
  };
  raiz.addEventListener('focusin', (e) => {
    const m = e.target && e.target.id && e.target.id.match(/^sp-cfg-cara-(\d+)$/);
    if (m) v.mirar(parseInt(m[1], 10));
  });
  /* La fuente de la marca puede llegar después: se vuelve a pintar. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => {
    v.caras.forEach((c) => { c.firma = ''; });
    v.actualizar(leerEstado(C));
  });
  v.actualizar(leerEstado(C));
}

function iniciar() {
  if (!soportaWebGL()) return;
  document.querySelectorAll('[data-sp-configurador]').forEach((raiz) => conectar(raiz, 0));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
else iniciar();
document.addEventListener('shopify:section:load', iniciar);
